import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, Linking, Platform } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { saveAs } from 'file-saver';
import { evidenceApi, Evidence } from '../../services/apiService';
import { FONTS } from '../../constants/config';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { getTextAlign } from '../../utils/rtl';
import { ChevronLeftIcon, ChevronRightIcon, EyeIcon, EditIcon, TrashIcon, PlusIcon, ExportIcon } from '../Icons';
import { tokens } from '@design';
import { Button } from '@design/components/Button';

const PAGE_SIZE = 20;

export interface EvidenceTableProps {
  relatedType: string;
  /** Provide for per-entity tables; omit for collection (aggregate) tables. */
  filterRelatedId?: number;
  title: string;
  /** Writer (org_employee/coordinator/superuser of this org). */
  canManage: boolean;
  /** Show the الجهة column (overview roles / collection aggregate). */
  showOrgColumn?: boolean;
  onAddPress?: () => void;
  onEditPress?: (evidence: Evidence) => void;
  /** Bump to force a refetch (after add/edit/delete elsewhere). */
  refreshSignal?: number;
}

export default function EvidenceTable({
  relatedType, filterRelatedId, title, canManage,
  showOrgColumn = false, onAddPress, onEditPress, refreshSignal = 0,
}: EvidenceTableProps) {
  const { t, isRTL } = useLanguage();
  const rtlRow = isRTL ? ('row-reverse' as const) : ('row' as const);
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();

  const [rows, setRows] = useState<Evidence[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const params = useCallback((p: number) => {
    const base: Record<string, any> = { related_type: relatedType, page: p, page_size: PAGE_SIZE };
    if (filterRelatedId != null) base.related_id = filterRelatedId;
    return base;
  }, [relatedType, filterRelatedId]);

  const fetchPage = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const data = await evidenceApi.list(params(p));
      setRows(data.results);
      setCount(data.count);
      setPage(p);
    } catch {
      showError(t('evidence.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [params, showError, t]);

  // Refetch on mount and whenever the host screen regains focus — e.g. after
  // returning from AddEvidenceScreen via goBack() so a fresh upload shows
  // immediately. refreshSignal still forces a refetch (delete elsewhere, etc.).
  useFocusEffect(
    useCallback(() => {
      fetchPage(1);
    }, [fetchPage, refreshSignal]),
  );

  const openFile = (ev: Evidence) => {
    const raw = ev.file || ev.external_link;
    if (!raw) return;
    let safeHref: string | null = null;
    try {
      const base = Platform.OS === 'web' ? window.location.origin : 'https://placeholder.local';
      const u = new URL(raw, base);
      if (u.protocol === 'http:' || u.protocol === 'https:') {
        safeHref = u.href;
      }
    } catch {
      safeHref = null;
    }
    if (!safeHref) return; // reject javascript:, data:, blob:, etc.
    if (Platform.OS === 'web') {
      window.open(safeHref, '_blank', 'noopener,noreferrer');
    } else {
      Linking.openURL(safeHref);
    }
  };

  const handleDelete = async (ev: Evidence) => {
    // Web-primary: native has no synchronous confirm; native delete proceeds without a prompt.
    // eslint-disable-next-line no-alert
    const ok = Platform.OS === 'web' ? window.confirm(t('evidence.deleteConfirm')) : true;
    if (!ok) return;
    try {
      const resp = await evidenceApi.delete(ev.id);
      showSuccess(resp.status === 202 ? t('evidence.deletePending') : t('evidence.deleteSuccess'));
      fetchPage(page);
    } catch {
      showError(t('evidence.deleteFailed'));
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await evidenceApi.exportZip(params(1));
      if (Platform.OS === 'web') saveAs(blob, 'evidence.zip');
    } catch {
      showError(t('evidence.loadFailed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <View style={[styles.wrapper, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
      {/* Header: title (right in RTL) + CTAs. Matches the system PageHeader
          actions pattern: primary Add first (left), secondary Export second
          (right), fixed row direction. */}
      <View style={[styles.headerRow, { flexDirection: rtlRow }]}>
        <Text style={[styles.title, { color: colors.textPrimary, textAlign: getTextAlign(isRTL) }]}>{title}</Text>
        <View style={{ flexDirection: 'row', gap: tokens.spacing.absolute['2'] }}>
          {canManage && onAddPress && (
            <Button variant="primary" onPress={onAddPress}
              trailingIcon={<PlusIcon size={16} color={tokens.colors.raw.white} />}>
              {t('evidence.addNew')}
            </Button>
          )}
          <Button variant="secondary" onPress={handleExport} loading={exporting}
            trailingIcon={<ExportIcon size={16} color={colors.textMuted} />}>
            {t('evidence.export')}
          </Button>
        </View>
      </View>

      {loading ? (
        <View style={styles.centered}><ActivityIndicator size="large" color={tokens.colors.semantic.general.foreground} /></View>
      ) : rows.length === 0 ? (
        <View style={styles.centered}><Text style={[styles.emptyText, { color: colors.textMuted }]}>{t('evidence.empty')}</Text></View>
      ) : (
        <View style={[styles.tableWrapper, { borderColor: colors.cardBorder }]}>
          <View style={[styles.tableRow, styles.tableHeader, { flexDirection: rtlRow, backgroundColor: colors.card, borderBottomColor: colors.cardBorder }]}>
            <Text style={[styles.headerCell, styles.colName, hc(colors, isRTL)]}>{t('evidence.name')}</Text>
            <Text style={[styles.headerCell, styles.colWide, hc(colors, isRTL)]}>{t('evidence.targetCriterion')}</Text>
            <Text style={[styles.headerCell, styles.colWide, hc(colors, isRTL)]}>{t('evidence.impact')}</Text>
            {showOrgColumn && <Text style={[styles.headerCell, styles.colOrg, hc(colors, isRTL)]}>{t('evidence.organization')}</Text>}
            <Text style={[styles.headerCell, styles.colUploader, hc(colors, isRTL)]}>{t('evidence.uploadedBy')}</Text>
            <Text style={[styles.headerCell, styles.colDate, hc(colors, isRTL)]}>{t('evidence.uploadedAt')}</Text>
            <Text style={[styles.headerCell, styles.colActions, { color: colors.textMuted }]}> </Text>
          </View>

          {rows.map((ev) => (
            <View key={ev.id} style={[styles.tableRow, { flexDirection: rtlRow, borderBottomColor: colors.border }]}>
              <Text style={[styles.cell, styles.colName, bc(colors, isRTL)]} numberOfLines={2}>{ev.title}</Text>
              <Text style={[styles.cell, styles.colWide, mc(colors, isRTL)]} numberOfLines={2}>{ev.target_criterion || '-'}</Text>
              <Text style={[styles.cell, styles.colWide, mc(colors, isRTL)]} numberOfLines={2}>{ev.impact || '-'}</Text>
              {showOrgColumn && <Text style={[styles.cell, styles.colOrg, mc(colors, isRTL)]} numberOfLines={1}>{ev.organization_name || '-'}</Text>}
              <Text style={[styles.cell, styles.colUploader, mc(colors, isRTL)]} numberOfLines={1}>{ev.uploaded_by_details?.full_name || '-'}</Text>
              <Text style={[styles.cell, styles.colDate, mc(colors, isRTL)]} numberOfLines={1}>{ev.uploaded_at ? new Date(ev.uploaded_at).toLocaleDateString() : ''}</Text>
              <View style={[styles.colActions, { flexDirection: rtlRow, gap: tokens.spacing.absolute['1'] }]}>
                <TouchableOpacity style={styles.iconBtn} onPress={() => openFile(ev)} accessibilityLabel={t('evidence.view')}>
                  <EyeIcon size={18} color={colors.textMuted} />
                </TouchableOpacity>
                {canManage && (
                  <>
                    <TouchableOpacity style={styles.iconBtn} onPress={() => onEditPress?.(ev)} accessibilityLabel={t('common.edit')}>
                      <EditIcon size={16} color={colors.textMuted} />
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.iconBtn} onPress={() => handleDelete(ev)} accessibilityLabel={t('common.delete')}>
                      <TrashIcon size={16} color={tokens.colors.raw.red['500']} />
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </View>
          ))}
        </View>
      )}

      {totalPages > 1 && (
        <View style={[styles.pagination, { flexDirection: rtlRow }]}>
          <TouchableOpacity style={[styles.pageBtn, page <= 1 && styles.pageBtnDisabled]} disabled={page <= 1} onPress={() => page > 1 && fetchPage(page - 1)}>
            <Text style={[styles.pageBtnText, { color: colors.textMuted }]}>{t('pagination.previous')}</Text>
            <ChevronRightIcon size={14} color={colors.textMuted} />
          </TouchableOpacity>
          <Text style={[styles.pageIndicator, { color: colors.textMuted }]}>{page} / {totalPages}</Text>
          <TouchableOpacity style={[styles.pageBtn, page >= totalPages && styles.pageBtnDisabled]} disabled={page >= totalPages} onPress={() => page < totalPages && fetchPage(page + 1)}>
            <ChevronLeftIcon size={14} color={colors.textMuted} />
            <Text style={[styles.pageBtnText, { color: colors.textMuted }]}>{t('pagination.next')}</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
}

const hc = (colors: any, isRTL: boolean) => ({ color: colors.textMuted, textAlign: getTextAlign(isRTL) });
const bc = (colors: any, isRTL: boolean) => ({ color: colors.textPrimary, textAlign: getTextAlign(isRTL) });
const mc = (colors: any, isRTL: boolean) => ({ color: colors.textMuted, textAlign: getTextAlign(isRTL) });

const styles = StyleSheet.create({
  wrapper: {
    marginTop: tokens.spacing.absolute['8'],
    borderWidth: tokens.spacing.absolute['px'],
    borderRadius: tokens.radii.absolute['radius-12'],
    paddingHorizontal: tokens.spacing.absolute['6'],
    paddingVertical: tokens.spacing.absolute['5'],
  },
  headerRow: { justifyContent: 'space-between', alignItems: 'center', marginBottom: tokens.spacing.absolute['4'] },
  // 'paragraph large font size' does not exist in tokens; use 'paragraph subheading font size' (18) as closest equivalent
  title: { fontFamily: FONTS.medium, fontSize: tokens.typography.paragraph['paragraph subheading font size'], fontWeight: '600' },
  tableWrapper: { borderWidth: 1, borderRadius: tokens.radii.absolute['radius-8'], overflow: 'hidden' },
  tableRow: { alignItems: 'center', borderBottomWidth: 1, paddingVertical: tokens.spacing.absolute['3'], paddingHorizontal: tokens.spacing.absolute['4'], gap: tokens.spacing.absolute['3'] },
  tableHeader: { borderBottomWidth: 1 },
  headerCell: { fontFamily: FONTS.medium, fontSize: tokens.typography.paragraph['paragraph mini font size'], fontWeight: '600' },
  cell: { fontFamily: FONTS.regular, fontSize: tokens.typography.paragraph['paragraph small font size'] },
  colName: { flex: 2, minWidth: 120 },
  colWide: { flex: 3, minWidth: 140 },
  colOrg: { width: 140, flexShrink: 0 },
  colUploader: { width: 120, flexShrink: 0 },
  colDate: { width: 100, flexShrink: 0 },
  colActions: { width: 120, flexShrink: 0, alignItems: 'center' },
  iconBtn: { padding: tokens.spacing.absolute['2'], minWidth: 36, minHeight: 36, justifyContent: 'center', alignItems: 'center' },
  centered: { justifyContent: 'center', alignItems: 'center', paddingVertical: tokens.spacing.absolute['12'] },
  emptyText: { fontFamily: FONTS.regular, fontSize: tokens.typography.paragraph['paragraph font size'] },
  pagination: { justifyContent: 'center', alignItems: 'center', gap: tokens.spacing.absolute['4'], marginTop: tokens.spacing.absolute['6'] },
  pageBtn: { flexDirection: 'row', alignItems: 'center', gap: tokens.spacing.absolute['1_5'], paddingHorizontal: tokens.spacing.absolute['3'], paddingVertical: tokens.spacing.absolute['2'], minHeight: 44 },
  pageBtnDisabled: { opacity: 0.4 },
  pageBtnText: { fontFamily: FONTS.regular, fontSize: tokens.typography.paragraph['paragraph small font size'] },
  pageIndicator: { fontFamily: FONTS.regular, fontSize: tokens.typography.paragraph['paragraph small font size'], minWidth: 60, textAlign: 'center' },
});
